<div class="space-y-5">
  @if (loading()) {
    <div
      class="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6 space-y-4 animate-pulse"
    >
      <div class="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
      <div class="h-28 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
    </div>
    <div
      class="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6 space-y-3 animate-pulse"
    >
      <div class="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
      <div class="h-14 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
      <div class="h-14 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
    </div>
  } @else if (error()) {
    <div
      class="rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/20 p-5 flex items-center gap-3"
    >
      <lucide-icon [img]="AlertCircle" class="w-5 h-5 text-red-500 shrink-0"></lucide-icon>
      <p class="text-sm text-red-700 dark:text-red-300 flex-1">{{ error() }}</p>
      <button
        (click)="load()"
        class="flex items-center gap-1 text-sm text-red-600 hover:underline shrink-0"
      >
        <lucide-icon [img]="RefreshCw" class="w-3.5 h-3.5"></lucide-icon>
        {{ 'SETTINGS.BILLING.RELOAD' | translate }}
      </button>
    </div>
  } @else if (billing()) {
    <!-- Current Plan -->
    <div
      class="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-950 dark:text-gray-100 shadow-sm"
    >
      <div class="p-5 pb-3">
        <h3 class="font-semibold text-base">{{ 'SETTINGS.BILLING.TITLE' | translate }}</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          {{ 'SETTINGS.BILLING.SUBTITLE' | translate }}
        </p>
      </div>
      <div class="px-5 pb-5">
        @if (billing()!.planName) {
          <div
            class="p-4 sm:p-5 border-2 rounded-xl space-y-4"
            [class]="
              isExpired()
                ? 'border-red-200 bg-red-50 dark:bg-red-900/10'
                : isExpiringSoon()
                  ? 'border-yellow-200 bg-yellow-50 dark:bg-yellow-900/10'
                  : 'border-green-200 bg-green-50 dark:bg-green-900/10'
            "
          >
            <div class="flex items-start justify-between gap-3">
              <div class="space-y-1 min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  <h3 class="text-lg font-bold text-gray-900 dark:text-gray-100">
                    {{ billing()!.planName }}
                  </h3>
                  @if (isExpired()) {
                    <span
                      class="px-2 py-0.5 text-xs bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-full font-medium"
                    >
                      {{ 'SETTINGS.BILLING.EXPIRED' | translate }}
                    </span>
                  } @else if (isExpiringSoon()) {
                    <span
                      class="px-2 py-0.5 text-xs bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 rounded-full font-medium"
                    >
                      {{ 'SETTINGS.BILLING.DAYS_LEFT' | translate: { days: daysRemaining() } }}
                    </span>
                  } @else {
                    <span
                      class="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded-full font-medium"
                    >
                      {{ 'SETTINGS.BILLING.ACTIVE' | translate }}
                    </span>
                  }
                </div>
                <p class="text-gray-600 dark:text-gray-400 font-medium text-sm">
                  {{ formatMoney(billing()!.monthlyPrice)
                  }}{{ 'SETTINGS.BILLING.PER_MONTH' | translate }}
                </p>
              </div>
              <lucide-icon [img]="CreditCard" class="w-7 h-7 text-green-400 shrink-0"></lucide-icon>
            </div>
            <div class="grid grid-cols-2 gap-2 text-sm">
              <div class="bg-white/70 dark:bg-gray-800/60 rounded-lg px-3 py-2">
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{ 'SETTINGS.BILLING.MAX_STORES' | translate }}
                </p>
                <p class="font-semibold text-gray-900 dark:text-gray-100">
                  {{ billing()!.maxStores ?? '—' }}
                </p>
              </div>
              <div class="bg-white/70 dark:bg-gray-800/60 rounded-lg px-3 py-2">
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{ 'SETTINGS.BILLING.MAX_EMPLOYEES' | translate }}
                </p>
                <p class="font-semibold text-gray-900 dark:text-gray-100">
                  {{ billing()!.maxEmployees ?? '—' }}
                </p>
              </div>
              <div class="bg-white/70 dark:bg-gray-800/60 rounded-lg px-3 py-2">
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{ 'SETTINGS.BILLING.STARTED' | translate }}
                </p>
                <p class="font-semibold text-gray-900 dark:text-gray-100 text-xs sm:text-sm">
                  {{ formatDate(billing()!.subscriptionStartDate) }}
                </p>
              </div>
              <div class="bg-white/70 dark:bg-gray-800/60 rounded-lg px-3 py-2">
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{ 'SETTINGS.BILLING.EXPIRES' | translate }}
                </p>
                <p
                  class="font-semibold text-xs sm:text-sm"
                  [class]="
                    isExpired()
                      ? 'text-red-600'
                      : isExpiringSoon()
                        ? 'text-yellow-600'
                        : 'text-gray-900 dark:text-gray-100'
                  "
                >
                  {{ formatDate(billing()!.subscriptionEndDate) }}
                </p>
              </div>
            </div>

            @if (isExpired() || isExpiringSoon()) {
              <div
                class="flex items-center gap-2 p-3 bg-white/60 dark:bg-gray-800/60 rounded-lg border border-current/20"
              >
                <lucide-icon
                  [img]="Clock"
                  class="w-4 h-4 shrink-0"
                  [class]="isExpired() ? 'text-red-500' : 'text-yellow-500'"
                ></lucide-icon>
                <p
                  class="text-xs"
                  [class]="
                    isExpired()
                      ? 'text-red-700 dark:text-red-300'
                      : 'text-yellow-700 dark:text-yellow-300'
                  "
                >
                  @if (isExpired()) {
                    {{ 'SETTINGS.BILLING.EXPIRED_MSG' | translate }}
                  } @else {
                    {{ 'SETTINGS.BILLING.EXPIRING_MSG' | translate: { days: daysRemaining() } }}
                  }
                </p>
              </div>
            }
          </div>
        } @else {
          <div
            class="p-8 text-center border border-dashed border-gray-200 dark:border-gray-700 rounded-xl"
          >
            <lucide-icon
              [img]="CreditCard"
              class="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2"
            ></lucide-icon>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              {{ 'SETTINGS.BILLING.NO_PLAN' | translate }}
            </p>
            <p class="text-xs text-gray-400 mt-1">
              {{ 'SETTINGS.BILLING.NO_PLAN_HINT' | translate }}
            </p>
          </div>
        }
      </div>
    </div>

    <!-- Billing History -->
    <div
      class="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-950 dark:text-gray-100 shadow-sm"
    >
      <div class="p-5 pb-3">
        <h3 class="font-semibold text-base">{{ 'SETTINGS.BILLING.HISTORY_TITLE' | translate }}</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          {{ 'SETTINGS.BILLING.HISTORY_SUBTITLE' | translate }}
        </p>
      </div>
      <div class="px-5 pb-5">
        @if (billing()!.payments.length === 0) {
          <div class="text-center py-8 text-gray-400">
            <lucide-icon [img]="CreditCard" class="w-7 h-7 mx-auto mb-2 opacity-40"></lucide-icon>
            <p class="text-sm">{{ 'SETTINGS.BILLING.HISTORY_EMPTY' | translate }}</p>
          </div>
        } @else {
          <div class="space-y-3">
            @for (payment of billing()!.payments; track payment.id) {
              <div
                class="flex items-start justify-between gap-3 p-4 border border-gray-100 dark:border-gray-800 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
              >
                <div class="space-y-0.5 min-w-0">
                  <p class="font-medium text-sm text-gray-900 dark:text-gray-100">
                    {{ payment.planName }}
                    <span class="text-gray-400 font-normal"
                      >&mdash;
                      {{ 'SETTINGS.BILLING.MONTHS' | translate: { months: payment.months } }}</span
                    >
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    {{ formatDate(payment.paidAt) }} &middot; {{ payment.paymentMethod }}
                  </p>
                  @if (payment.discountPercent > 0) {
                    <p class="text-xs text-green-600 dark:text-green-400">
                      {{
                        'SETTINGS.BILLING.DISCOUNT'
                          | translate: { percent: payment.discountPercent }
                      }}
                      (&minus;{{ formatMoney(payment.discountAmount) }})
                    </p>
                  }
                  @if (payment.notes) {
                    <p class="text-xs text-gray-400 italic truncate">{{ payment.notes }}</p>
                  }
                </div>
                <div class="text-right shrink-0">
                  <p class="font-semibold text-sm text-gray-900 dark:text-gray-100">
                    {{ formatMoney(payment.totalAmount) }}
                  </p>
                  @if (payment.discountPercent > 0) {
                    <p class="text-xs text-gray-400 line-through">
                      {{ formatMoney(payment.baseAmount) }}
                    </p>
                  }
                  <div class="flex items-center gap-1 justify-end mt-1">
                    <lucide-icon [img]="CheckCircle" class="w-3 h-3 text-green-500"></lucide-icon>
                    <span class="text-xs text-green-600 dark:text-green-400">{{
                      'SETTINGS.BILLING.PAID' | translate
                    }}</span>
                  </div>
                </div>
              </div>
            }
          </div>
        }
      </div>
    </div>
  }
</div>
